import React from "react";
import {
  ArchiveOutlined,
  BooksTwoOutlined,
  DeleteOutlined,
  PencilLineOutlined,
  PlaceholderFileUpArrowOutlined,
  RefreshArrowOutlined,
} from "@toddle-edu/ds-icons";
import {
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM,
  NODE_TYPE_IB_MYP_CURRICULUM_PROGRAM,
  NODE_TYPE_UBD_CURRICULUM_PROGRAM,
  NODE_TYPE_IB_DP_CURRICULUM_PROGRAM,
  SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";

export const ACADEMIC_SETUP_TYPES = {
  SUBJECT_BASED_GRADING: SUBJECT_BASED_GRADING,
  COURSE_BASED_GRADING: COURSE_BASED_GRADING,
  OLD_SUBJECT_BASED_GRADING: "OLD_SUBJECT_BASED_GRADING", // Specific to standards
};

export const STATUS_FILTERS = {
  PUBLISHED: "PUBLISHED",
  DRAFT: "DRAFT",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  ARCHIVED: "ARCHIVED",
  PARTIALLY_PUBLISHED: "PARTIALLY_PUBLISHED",
};

export const MODAL_TYPES = {
  IMPORT_STANDARD: "IMPORT_STANDARD",
  UPLOAD_STANDARD: "UPLOAD_STANDARD",
  CREATE_NEW_STANDARD: "CREATE_NEW_STANDARD",
  RENAME_STANDARD: "RENAME_STANDARD",
  ARCHIVE_STANDARD: "ARCHIVE_STANDARD",
  RESTORE_STANDARD: "RESTORE_STANDARD",
};

export const DIALOG_TYPES = {
  DELETE_STANDARD: "DELETE_STANDARD",
};

export const STANDARD_ACTIONS = {
  RENAME: MODAL_TYPES.RENAME_STANDARD,
  ARCHIVE: MODAL_TYPES.ARCHIVE_STANDARD,
  DELETE: DIALOG_TYPES.DELETE_STANDARD,
  RESTORE: MODAL_TYPES.RESTORE_STANDARD,
};

export const FEEDLIST_COLUMN_IDS = {
  NAME: "NAME",
  SUBJECT_NAME: "SUBJECT_NAME",
  TAGGED_IN_COURSES: "TAGGED_IN_COURSES",
  STANDARDS_COUNT: "STANDARDS_COUNT",
  STATUS: "STATUS",
  MODIFIED_BY_DETAILS: "MODIFIED_BY_DETAILS",
  ACTIONS: "ACTIONS",
  NAVIGATOR: "NAVIGATOR",
};

export const FEEDLIST_UI_TYPES = {
  TEXT: "TEXT",
  BUTTON: "BUTTON",
  USER_NAME_WITH_AVATAR: "USER_NAME_WITH_AVATAR",
  STANDARD_ACTIONS: "STANDARD_ACTIONS",
  TEXT_WITH_ICON: "TEXT_WITH_ICON",
  NUMBER_TEXT: "NUMBER_TEXT",
  TAGGED_COURSES_POPOVER: "TAGGED_COURSES_POPOVER",
};

export const ADD_DROPDOWN_OPTIONS = t => [
  {
    key: MODAL_TYPES.IMPORT_STANDARD,
    label: t("scopeAndSequence:import_label_from_library", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    subText: t("scopeAndSequence:choose_from_range_of_label", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    icon: <BooksTwoOutlined />,
  },
  {
    key: MODAL_TYPES.UPLOAD_STANDARD,
    label: t("scopeAndSequence:bulk_upload_label", {
      label: t("scopeAndSequence:standard_set_other"),
    }),
    subText: t("scopeAndSequence:download_template_file_description"),
    icon: <PlaceholderFileUpArrowOutlined />,
  },
  {
    key: MODAL_TYPES.CREATE_NEW_STANDARD,
    label: t("scopeAndSequence:create_standard_set_on_toddle"),
    subText: t("scopeAndSequence:create_subject_standards_interface"),
    icon: <PencilLineOutlined />,
  },
];

export const STATUS_OPTIONS = t => [
  {
    value: STATUS_FILTERS.PUBLISHED,
    label: t("common:published"),
  },
  {
    value: STATUS_FILTERS.PARTIALLY_PUBLISHED,
    label: t("scopeAndSequence:partially_published"),
    tooltipLabel: t("scopeAndSequence:partially_published_tooltip"),
  },
  {
    value: STATUS_FILTERS.DRAFT,
    label: t("common:draft"),
  },
  {
    value: STATUS_FILTERS.NOT_CONFIGURED,
    label: t("common:not_configured_yet"),
  },
  {
    value: STATUS_FILTERS.ARCHIVED,
    label: t("common:archived"),
  },
];

export const STANDARD_ACTIONS_OPTIONS = ({ t, coursesCount }) => [
  {
    key: STANDARD_ACTIONS.RENAME,
    label: t("common:rename"),
    icon: <PencilLineOutlined />,
  },
  {
    key: STANDARD_ACTIONS.ARCHIVE,
    label: t("common:archive"),
    icon: <ArchiveOutlined />,
  },
  {
    key: STANDARD_ACTIONS.DELETE,
    label: t("common:delete"),
    icon: <DeleteOutlined />,
    isDestructive: true,
    disabled: coursesCount > 0,
    disabledTooltip: t(
      "scopeAndSequence:standard_set_delete_disabled_tooltip",
      {
        count: coursesCount || 0,
      }
    ),
    tooltipPlacement: "left",
  },
];

export const STANDARD_ACTIONS_ARCHIVED_OPTIONS = t => [
  {
    key: STANDARD_ACTIONS.RESTORE,
    label: t("common:restore"),
    icon: <RefreshArrowOutlined />,
  },
];

export const FEEDLIST_COLUMN_LIST = ({ t, isSubjectGroupingAllowed }) => ({
  [FEEDLIST_COLUMN_IDS.SUBJECT_NAME]: {
    id: FEEDLIST_COLUMN_IDS.SUBJECT_NAME,
    type: "CUSTOM",
    label: t("common:subject_name"),
    uiType: FEEDLIST_UI_TYPES.TEXT_WITH_ICON,
    style: {
      minWidth: "240px",
      maxWidth: "240px",
      width: "100%",
    },
    ...(!isSubjectGroupingAllowed
      ? {
          sortType: "toggle",
          entity: "TITLE",
        }
      : {}),
  },
  [FEEDLIST_COLUMN_IDS.NAME]: {
    id: FEEDLIST_COLUMN_IDS.NAME,
    type: "CUSTOM",
    label: t("common:name"),
    uiType: FEEDLIST_UI_TYPES.TEXT_WITH_ICON,
    style: {
      minWidth: "240px",
      maxWidth: "240px",
      width: "100%",
    },
    sortType: "toggle",
    entity: "TITLE",
  },
  [FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES]: {
    id: FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    type: "CUSTOM",
    label: t("scopeAndSequence:tagged_in_courses"),
    uiType: FEEDLIST_UI_TYPES.TAGGED_COURSES_POPOVER,
    style: {
      width: "120px",
    },
  },
  [FEEDLIST_COLUMN_IDS.STANDARDS_COUNT]: {
    id: FEEDLIST_COLUMN_IDS.STANDARDS_COUNT,
    type: "CUSTOM",
    label: t("scopeAndSequence:no_of_stanadrds"),
    uiType: FEEDLIST_UI_TYPES.NUMBER_TEXT,
    style: {
      width: "108px",
    },
  },
  [FEEDLIST_COLUMN_IDS.STATUS]: {
    id: FEEDLIST_COLUMN_IDS.STATUS,
    type: "CUSTOM",
    label: t("common:status"),
    uiType: FEEDLIST_UI_TYPES.STATUS_TAG,
    style: {
      width: "40px",
      paddingLeft: "12px",
    },
  },
  [FEEDLIST_COLUMN_IDS.MODIFIED_BY_DETAILS]: {
    id: FEEDLIST_COLUMN_IDS.MODIFIED_BY_DETAILS,
    label: t("scopeAndSequence:last_updated_by"),
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.USER_NAME_WITH_AVATAR,
    style: {
      width: "220px",
    },
  },
  [FEEDLIST_COLUMN_IDS.ACTIONS]: {
    id: FEEDLIST_COLUMN_IDS.ACTIONS,
    label: t("common:action_plural"),
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.STANDARD_ACTIONS,
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "88px",
    },
  },
  [FEEDLIST_COLUMN_IDS.NAVIGATOR]: {
    id: FEEDLIST_COLUMN_IDS.NAVIGATOR,
    label: "",
    type: "CUSTOM",
    uiType: FEEDLIST_UI_TYPES.BUTTON,
    style: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "72px",
    },
  },
});

export const FEEDLIST_COLUMN_LIST_BY_GRADING_TYPE = {
  [ACADEMIC_SETUP_TYPES.SUBJECT_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.SUBJECT_NAME,
    FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    FEEDLIST_COLUMN_IDS.STANDARDS_COUNT,
    FEEDLIST_COLUMN_IDS.MODIFIED_BY_DETAILS,
    FEEDLIST_COLUMN_IDS.NAVIGATOR,
  ],
  [ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.NAME,
    FEEDLIST_COLUMN_IDS.TAGGED_IN_COURSES,
    FEEDLIST_COLUMN_IDS.STANDARDS_COUNT,
    FEEDLIST_COLUMN_IDS.MODIFIED_BY_DETAILS,
    FEEDLIST_COLUMN_IDS.ACTIONS,
  ],
  [ACADEMIC_SETUP_TYPES.OLD_SUBJECT_BASED_GRADING]: [
    FEEDLIST_COLUMN_IDS.STATUS,
    FEEDLIST_COLUMN_IDS.SUBJECT_NAME,
    FEEDLIST_COLUMN_IDS.STANDARDS_COUNT,
    FEEDLIST_COLUMN_IDS.MODIFIED_BY_DETAILS,
    FEEDLIST_COLUMN_IDS.NAVIGATOR,
  ],
};

export const DIALOG_DETAILS = t => ({
  [DIALOG_TYPES.DELETE_STANDARD]: {
    title: t("common:delete_with_label", {
      label: t("scopeAndSequence:standard_set"),
    }),
    message: t("scopeAndSequence:delete_standard_set_warning"),
    buttonOneText: t("common:cancel"),
    buttonTwoText: t("common:delete_with_label", {
      label: t("scopeAndSequence:standard_set"),
    }),
  },
});

export const CURRICULUM_PROGRAM_TO_LEARNING_STANDARD_MAP = {
  [NODE_TYPE_IB_PYP_CURRICULUM_PROGRAM]: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  [NODE_TYPE_IB_MYP_CURRICULUM_PROGRAM]: ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  [NODE_TYPE_UBD_CURRICULUM_PROGRAM]: ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  [NODE_TYPE_IB_DP_CURRICULUM_PROGRAM]: ELEMENT_TYPE_DP_LEARNING_STANDARD,
};

export const IMPORT_FROM_TODDLE_LIBRARY_TYPES = {
  PUBLISHED_STANDARDS: "PUBLISHED_STANDARDS",
  DRAFT_STANDARDS: "DRAFT_STANDARDS",
};

export const CURRICULUM_PROGRAM_TO_STANDARDS_CHILD_ID_MAP = {
  [CURRICULUM_TYPE_PYP]: "PYP_STANDARDS_V2",
  [CURRICULUM_TYPE_MYP]: "MYP_STANDARDS_V2",
  [CURRICULUM_TYPE_UBD]: "UBD_STANDARDS_V2",
  [CURRICULUM_TYPE_DP]: "DP_STANDARDS_V2",
};
